// src/components/planning/ServiceCard/components/SongsSection.tsx

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { SongsSectionProps } from '../../types/componentProps';
import { FileIndicator } from '../../../common/FileIndicator';
import componentStyles from '../styles/components.module.css';
import { MusicFilterState, PerformanceType } from '../../../Header/PerformanceTypeFilter';
import apiClient from '../../../../utils/apiClient';
import { toast } from 'react-toastify';
import { SongReminderBadge } from '../../SongReminders';

/**
 * What the version adds to the title, or '' when it adds nothing. Versions are
 * often the title again ("Let All Mortal Flesh"), the title with a verse code
 * ("O Come, O Come, Emmanuel (VS_1)") or just "Main"; only the part that says
 * something new is shown, with a verse code written out.
 */
export const versionNote = (title: string | undefined, versionTitle: string | undefined): string => {
  const v = (versionTitle || '').trim();
  if (!v || /^main$/i.test(v)) return '';
  const norm = (t: string) => t.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const nt = norm(title || '');
  const nv = norm(v);
  if (nt && (nv === nt || nt.startsWith(nv))) return '';
  // Drop the repeated title from the front, then any separator or bracket around what is left.
  let rest = v;
  if (nt && nv.startsWith(nt + ' ')) {
    const words = nt.split(' ').length;
    const m = v.match(new RegExp('^(?:[^a-z0-9]*[a-z0-9]+){' + words + '}', 'i'));
    rest = m ? v.slice(m[0].length) : v;
  }
  rest = rest.replace(/^[\s\-–·]+/, '').trim();
  if (/^\(.*\)$/.test(rest)) rest = rest.slice(1, -1).trim();
  if (/^main$/i.test(rest)) return '';
  const verses = rest.match(/^(?:verses?\s*)?\(?vs[_\s]*(\d+)\)?$/i);
  if (verses) {
    const digits = verses[1].split('');
    return digits.length > 1 ? `verses ${digits.join(', ')}` : `verse ${digits[0]}`;
  }
  return rest;
};

export const SongsSection: React.FC<SongsSectionProps> = ({
  serviceId,
  selections,
  spanishSelections = [],
  onEdit,
  liturgicalClass,
  filterState = { hideServiceMusic: false, selectedPerformanceTypes: [] }
}) => {
  const [performanceTypes, setPerformanceTypes] = useState<PerformanceType[]>([]);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  // The one song whose PDF is being fetched, if any
  const [downloadingSelectionId, setDownloadingSelectionId] = useState<number | null>(null);

  // Fetch performance types on mount
  useEffect(() => {
    const fetchPerformanceTypes = async () => {
      try {
        const response = await apiClient.get('/performance-types?active=true');
        setPerformanceTypes(response.data);
      } catch (error) {
        console.error('Error fetching performance types:', error);
      }
    };

    fetchPerformanceTypes();
  }, []);

  // Filter selections based on filter state - moved before handleDownloadPdf
  const filteredSelections = useMemo(() => {
    let result = selections;
    const { hideServiceMusic, selectedPerformanceTypes } = filterState;

    // If specific performance types are selected, filter by them
    if (selectedPerformanceTypes.length > 0) {
      // Map selected IDs to performance type names
      const selectedTypeNames = selectedPerformanceTypes
        .map(id => performanceTypes.find(pt => pt.id === id)?.name)
        .filter(Boolean);

      result = result.filter(selection => {
        // Match by performance type name
        return selectedTypeNames.some(typeName =>
          selection.performance_type === typeName
        );
      });
    }

    // Apply "hide service music" filter
    if (hideServiceMusic) {
      result = result.filter(selection => !selection.is_template_music);
    }

    return result;
  }, [selections, filterState, performanceTypes]);

  /** Save a PDF response under the name the server gave it. */
  const saveResponse = (response: any, fallbackName: string) => {
    const contentDisposition = response.headers['content-disposition'];
    let filename = fallbackName;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  };

  /** Download the PDF of one song in this service. */
  const handleDownloadSongPdf = async (selectionId: number, title: string) => {
    if (downloadingSelectionId !== null) return;
    setDownloadingSelectionId(selectionId);
    try {
      const response = await apiClient.get(
        `/pdf-compilation/services/${serviceId}/compile-pdf?selectionId=${selectionId}`,
        { responseType: 'blob' }
      );
      if (response.headers['x-songs-not-found']) {
        toast.error(`No file found for ${title}`);
        return;
      }
      saveResponse(response, `${title}.pdf`);
    } catch (error: any) {
      console.error('Error downloading song PDF:', error);
      toast.error(error.response?.status === 404 ? `No file found for ${title}` : `Could not download ${title}`);
    } finally {
      setDownloadingSelectionId(null);
    }
  };

  const handleDownloadPdf = async () => {
    // Check filtered selections, not all selections
    if (filteredSelections.length === 0) {
      toast.warning('No songs to compile into PDF (check your filters)');
      return;
    }

    setIsDownloadingPdf(true);
    try {
      // Build query params to pass filter state to backend
      const params = new URLSearchParams();
      if (filterState.hideServiceMusic) {
        params.append('hideServiceMusic', 'true');
      }
      if (filterState.selectedPerformanceTypes.length > 0) {
        // Map IDs to names for backend filtering
        const selectedTypeNames = filterState.selectedPerformanceTypes
          .map(id => performanceTypes.find(pt => pt.id === id)?.name)
          .filter(Boolean);
        if (selectedTypeNames.length > 0) {
          params.append('performanceTypes', selectedTypeNames.join(','));
        }
      }

      const queryString = params.toString();
      const apiUrl = `/pdf-compilation/services/${serviceId}/compile-pdf${queryString ? '?' + queryString : ''}`;

      const response = await apiClient.get(apiUrl, { responseType: 'blob' });

      // Check for missing songs header
      const missingSongsHeader = response.headers['x-songs-not-found'];
      if (missingSongsHeader) {
        const missingSongs = JSON.parse(missingSongsHeader);
        if (missingSongs.length > 0) {
          toast.info(`PDF generated. Missing files for: ${missingSongs.join(', ')}`);
        }
      }

      saveResponse(response, 'service_music.pdf');

      toast.success('PDF downloaded successfully');
    } catch (error: any) {
      console.error('Error downloading PDF:', error);
      if (error.response?.status === 404) {
        toast.error('No files found for songs in this service');
      } else {
        toast.error('Failed to download PDF');
      }
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const { hideServiceMusic, selectedPerformanceTypes } = filterState;

  return (
    <div className="songs-section">
    <div className={componentStyles.sectionHeader}>
      <h4>Selections</h4>
      {(hideServiceMusic || selectedPerformanceTypes.length > 0) && filteredSelections.length !== selections.length && (
        <span className="badge bg-info me-2" style={{ fontSize: '0.7rem' }}>
          Filtered: {filteredSelections.length}/{selections.length}
        </span>
      )}
      <div className={componentStyles.headerActions}>
        <i
          className={`bi bi-file-earmark-pdf ${componentStyles.pdfIcon} ${isDownloadingPdf ? componentStyles.loading : ''}`}
          onClick={!isDownloadingPdf ? handleDownloadPdf : undefined}
          title={filteredSelections.length < selections.length
            ? `Download PDF of ${filteredSelections.length} filtered songs`
            : 'Download PDF of all songs'}
          style={{ cursor: isDownloadingPdf ? 'wait' : 'pointer' }}
        />
        <i
          className={`bi bi-pencil-square ${componentStyles.editIcon}`}
          onClick={onEdit}
        />
      </div>
    </div>
    <div className={componentStyles.container}>
      {filteredSelections.map((selection, index) => {
        const performanceType = selection.performance_type || 'Congregational Hymn';
        // The choir's pieces carry the one colour; the version shows only when it says something the title doesn't.
        const isAnthem = /anthem|choir/i.test(performanceType);
        const version = versionNote(selection.song_title, selection.version_title);
        const showVersion = version !== '';

        return (
          <div
            key={`song-${serviceId}-${selection.service_selection_id || index}`}
            className={`${componentStyles.item} ${isAnthem ? componentStyles.anthem : ''} ${selection.is_template_music ? componentStyles.template : ''}`}
            title={performanceType}
          >
            <p className={componentStyles.itemLabel}>{selection.placement}</p>
            <p className={componentStyles.itemContent}>
              <Link to={`/browse?tab=songs&songId=${selection.song_id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                {selection.song_title}
                {showVersion && (
                  <span className={componentStyles.versionTitle}> · {version}</span>
                )}
              </Link>
              <SongReminderBadge songId={selection.song_id} title={selection.song_title} />
              <span className={componentStyles.fileMark}>
                {selection.has_file && selection.service_selection_id && (
                  <i
                    className={`bi bi-file-earmark-pdf ${componentStyles.songPdfIcon} ${downloadingSelectionId === selection.service_selection_id ? componentStyles.loading : ''}`}
                    onClick={() => handleDownloadSongPdf(selection.service_selection_id!, selection.song_title)}
                    title={`Download PDF of ${selection.song_title}`}
                    role="button"
                    aria-label={`Download PDF of ${selection.song_title}`}
                  />
                )}
                <FileIndicator hasFile={selection.has_file} />
              </span>
            </p>
          </div>
        );
      })}
    </div>

    {spanishSelections.length > 0 && (
      <>
        <div className={componentStyles.sectionHeader}>
          <h4>Spanish selections (12:30)</h4>
        </div>
        <div className={componentStyles.container}>
          {[...spanishSelections]
            .sort((a, b) => a.order_number - b.order_number)
            .map((sel) => (
              <div
                key={`sp-${serviceId}-${sel.spanish_song_selection_id}`}
                className={componentStyles.item}
              >
                <p className={componentStyles.itemLabel}>{sel.placement}</p>
                <p className={componentStyles.itemContent}>{sel.song_title}</p>
              </div>
            ))}
        </div>
      </>
    )}
  </div>
  );
};
